/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module CportalModule
 */

import {
    IALBServicesAssetDetails,
    IALBServicesConfig,
    IALBServicesStatus,
} from 'generated-types';

import { CentralLicenseAlertsService, DevLoggerService } from 'ng/modules/core/services';
import { ComponentStore } from '@ngrx/component-store';
import { CportalService } from 'ajs/modules/cportal/services';
import { defaultPortalUrl } from 'ajs/modules/cportal/constants';
import { ILicensingStatusResponse } from 'ng/modules/licensing/licensing.types';
import { Injectable } from '@angular/core';
import { LicensingService } from 'ajs/modules/licensing';

/**
 * State definition. Contains Cloud Service data.
 */
interface ICPortalPageStoreState {
    albServicesConfig?: IALBServicesConfig,
    licenseState?: ILicensingStatusResponse,
    albServiceStatus?: IALBServicesStatus;
    assetDetails?: IALBServicesAssetDetails;
    error?: string;
}

/** Default state of the Cloud Service Store */
const initialState: ICPortalPageStoreState = {
    albServicesConfig: undefined,
    licenseState: undefined,
    albServiceStatus: undefined,
    error: undefined,
};

/**
 * @description Component Store for Cloud Service Page.
 * @author Deepak Chaudhary
 */
@Injectable()
export class CPortalPageStore extends ComponentStore<ICPortalPageStoreState> {
    public readonly portalUrl$ = this.select(state => state?.albServicesConfig?.portal_url ?? defaultPortalUrl);
    public readonly useSplitProxyStatus$ = this.select(state => state?.albServicesConfig?.use_split_proxy);
    public readonly albServiceStatus$ = this.select(state => state?.albServiceStatus);
    public readonly assetDetails$ = this.select(state => state?.assetDetails);

    public readonly siteId$ = this.select(state => {
        const { site_id: siteId, site_name: siteName } = state?.albServiceStatus?.asset_details?.site ?? {};

        if (siteId && siteName) {
            return `${siteId}:${siteName}`;
        } else if (siteId && !siteName) {
            return siteId;
        } else if (!siteId && siteName) {
            return siteName;
        }

        return '-';
    });

    public readonly error$ = this.select(state => state?.error);

    public readonly pulseCloudServiceOrgDashboardsVisibility$ = this.select(state => state?.albServicesConfig
        ?.feature_opt_in_status?.enable_pulse_inventory);

    public readonly pulseWafManagementStatus$ = this.select(state => state?.albServicesConfig
        ?.feature_opt_in_status?.enable_pulse_waf_management);

    public readonly appSignatureSyncStatus$ = this.select(state => state?.albServicesConfig
        ?.feature_opt_in_status?.enable_appsignature_sync);

    public readonly ipReputationStatus$ = this.select(state => state?.albServicesConfig
        ?.feature_opt_in_status?.enable_ip_reputation);

    public readonly userAgentDbSyncStatus$ = this.select(state => state?.albServicesConfig
        ?.feature_opt_in_status?.enable_user_agent_db_sync);

    public readonly wafSignaturesNotificationsStatus$ = this.select(state => state?.albServicesConfig
        ?.waf_config?.enable_waf_signatures_notifications);

    public readonly autoDownloadWafSignaturesStatus$ = this.select(state => state?.albServicesConfig
        ?.waf_config?.enable_auto_download_waf_signatures);

    /**
     * State is initialized with System Config.
     */
    constructor(
        private readonly cPortalService: CportalService,
        private readonly devLoggerService: DevLoggerService,
        private readonly licensingService: LicensingService,
        private readonly centralLicenseAlertsService: CentralLicenseAlertsService,
    ) {
        super(initialState);
    }

    /**
     * Set error message in store state.
     */
    public setError(error: string): void {
        this.patchState({ error });
    }

    /**
     * Clear error message from store state.
     */
    public clearError(): void {
        this.patchState({ error: undefined });
    }

    public async loadPortalInfo(): Promise<void> {
        try {
            const albServicesConfig = await this.cPortalService.getPortalInfo();

            this.patchState({ albServicesConfig });
        } catch (error) {
            this.devLoggerService.error(error);
        }
    }

    /**
     * Load licensing status from Licensing Service.
     */
    public async loadLicenseStatus(): Promise<void> {
        try {
            const licenseState: ILicensingStatusResponse = await this.licensingService.loadLicenseStatus();

            this.patchState({ licenseState });
        } catch (error) {
            this.devLoggerService.error(error);
        }
    }

    /**
     * Load the registration status from the cPortal Service.
     * The registration status is used to determine if the controller is registered.
     * If the controller is registered, the function will set the albServiceStatus to the registration status.
     */
    public async loadConnectivityStatus(): Promise<void> {
        try {
            await this.cPortalService.loadRegistrationStatus();

            const albServiceStatus = this.cPortalService.getRegistrationStatusDetails();
            const {
                asset_details: assetDetails,
                registration_status: registrationStatus,
            } = albServiceStatus;

            this.centralLicenseAlertsService.controllerRegistrationStatus = registrationStatus;
            this.patchState({
                albServiceStatus,
                assetDetails,
            });
        } catch (error) {
            this.devLoggerService.error(error);
            throw error;
        }
    }

    /**
     * Clear the registration state when controller is connected but not yet registered.
     */
    public async clearRegistration(): Promise<void> {
        try {
            await this.cPortalService.clearRegistration();
        } catch (error) {
            this.devLoggerService.error(error);
            throw error;
        }
    }
}
